import { SearchOutlined } from "@ant-design/icons";
import React, { useEffect, useState, useCallback } from "react";
import { Input, Select, Spin, Badge } from "antd";
import { MessageSquare } from "lucide-react";
import supportApi from "../../../api/supportApi";
import "../../../components/AddUserModal/style.css";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const statusTone = {
    OPEN: "border-rose-400/35 bg-rose-500/12 text-rose-100",
    IN_PROGRESS: "border-sky-400/35 bg-sky-500/12 text-sky-100",
    WAITING_USER: "border-amber-400/35 bg-amber-500/12 text-amber-100",
    WAITING_SUPPORT: "border-violet-400/35 bg-violet-500/12 text-violet-100",
    CLOSED: "border-white/15 bg-white/[0.08] text-white/55",
};

const statusLabels = {
    OPEN: "Открыт",
    IN_PROGRESS: "В работе",
    WAITING_USER: "Ожидает пользователя",
    WAITING_SUPPORT: "Ожидает саппорта",
    CLOSED: "Закрыт",
};

const Sidebar = ({ onSelectChat, selectedTicketId, refreshTicketsRef }) => {
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);

    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            const data = await supportApi.getTickets(statusFilter);

            let ticketsArray = [];
            if (Array.isArray(data)) {
                ticketsArray = data;
            } else if (data && Array.isArray(data.data)) {
                ticketsArray = data.data;
            } else if (data && data.tickets && Array.isArray(data.tickets)) {
                ticketsArray = data.tickets;
            }

            setTickets(ticketsArray);
            setFilteredTickets(ticketsArray);
        } catch (error) {
            console.error("Ошибка при загрузке тикетов:", error);
            setTickets([]);
            setFilteredTickets([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        if (refreshTicketsRef) {
            refreshTicketsRef.current = fetchTickets;
        }
    }, [fetchTickets, refreshTicketsRef]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredTickets(tickets);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = tickets.filter(
            (ticket) =>
                ticket.telegramUsername?.toLowerCase().includes(query) ||
                ticket.telegramId?.toString().includes(query) ||
                ticket.userId?.toString().includes(query)
        );
        setFilteredTickets(filtered);
    }, [searchQuery, tickets]);

    const formatTime = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return "только что";
            if (diffMins < 60) return `${diffMins} мин`;
            if (diffHours < 24) return `${diffHours} ч`;
            if (diffDays < 7) return `${diffDays} дн`;

            return new Intl.DateTimeFormat("ru-RU", {
                day: "2-digit",
                month: "2-digit",
            }).format(date);
        } catch {
            return "";
        }
    };

    const getUnreadCount = () => 0;

    return (
        <aside
            className={cn(
                shellClass,
                "flex h-auto min-h-[200px] max-h-[min(42vh,360px)] w-full max-w-none shrink-0 flex-col md:max-h-none md:h-full md:min-h-0 md:max-w-[22rem]"
            )}
        >
            <div className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full bg-indigo-500/10 blur-3xl" />

            <div className="relative shrink-0 border-b border-white/10 p-4">
                <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                        <MessageSquare className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                    </span>
                    <div className="min-w-0">
                        <h2 className="text-[15px] font-semibold tracking-tight text-white/90">Тикеты</h2>
                        <p className="text-[11px] text-white/40">Support inbox</p>
                    </div>
                </div>

                <Select
                    placeholder="Все статусы"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allowClear
                    popupClassName="glass-ant-select-dropdown"
                    className="w-full [&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selector]:!bg-white/[0.06] [&_.ant-select-selection-placeholder]:!text-white/35 [&_.ant-select-selection-item]:!text-white/88 [&_.ant-select-arrow]:!text-white/45"
                >
                    <Select.Option value="OPEN">Открыт</Select.Option>
                    <Select.Option value="IN_PROGRESS">В работе</Select.Option>
                    <Select.Option value="WAITING_USER">Ожидает пользователя</Select.Option>
                    <Select.Option value="WAITING_SUPPORT">Ожидает саппорта</Select.Option>
                    <Select.Option value="CLOSED">Закрыт</Select.Option>
                </Select>
            </div>

            <div className="relative shrink-0 px-4 pb-3 pt-2">
                <Input
                    placeholder="Поиск по username, Telegram ID…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                    prefix={<SearchOutlined className="text-white/35" />}
                    className="!rounded-xl !border-white/10 !bg-white/[0.06] py-2 text-white/90 placeholder:!text-white/35"
                />
            </div>

            <div className="relative min-h-0 flex-1 overflow-y-auto px-2 pb-3">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Spin className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                ) : filteredTickets.length === 0 ? (
                    <div className="px-3 py-10 text-center text-[13px] text-white/45">
                        {searchQuery ? "Ничего не найдено" : "Нет тикетов"}
                    </div>
                ) : (
                    <div className="space-y-1.5 px-1">
                        {filteredTickets.map((ticket) => {
                            const isSelected = selectedTicketId === ticket.id;
                            const unreadCount = getUnreadCount(ticket);
                            const pillClass = statusTone[ticket.status] || statusTone.CLOSED;

                            return (
                                <button
                                    type="button"
                                    key={ticket.id}
                                    onClick={() => onSelectChat(ticket)}
                                    className={cn(
                                        "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all",
                                        isSelected
                                            ? "border-emerald-400/40 bg-emerald-500/15 shadow-[0_8px_28px_-12px_rgba(16,185,129,0.35)]"
                                            : "border-transparent bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.06]"
                                    )}
                                >
                                    <div className="relative shrink-0">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.08] text-[15px] font-semibold text-white/85">
                                            {ticket.telegramUsername?.[0]?.toUpperCase() || "·"}
                                        </div>
                                        {unreadCount > 0 && (
                                            <Badge count={unreadCount} className="absolute -right-1 -top-1" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-1 flex items-center justify-between gap-2">
                                            <h3 className="truncate text-[13px] font-semibold text-white/90">
                                                {ticket.telegramUsername || `ID: ${ticket.userId}`}
                                            </h3>
                                            <span className="shrink-0 text-[10px] text-white/35">
                                                {formatTime(ticket.lastMessageAt || ticket.createdAt)}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span
                                                className={cn(
                                                    "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                                                    pillClass
                                                )}
                                            >
                                                {statusLabels[ticket.status] || ticket.status}
                                            </span>
                                            {ticket.assignedToUserId && (
                                                <span className="text-[10px] text-white/40">Назначен</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
